import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { DataTable, Pagination } from 'grommet';
import { DocumentText, FormDown, FormNext, Print } from 'grommet-icons';
import {
  ApiError,
  IncentiveStatementService,
  ProgramService,
  TListIncentiveStatementReportingPeriodsRequest,
} from '/src/api';
import { Box, Card, CardBody, CardHeader, DataTableHeader, ExternalLink, LoadingSpinner, Text } from '/src/components';
import { useGlobalStore, useUserStore } from '/src/context';
import {
  Client,
  IncentiveStatementListItem,
  IncentiveStatementReportingPeriod,
  Program,
  TClientId,
} from '/src/lib/models';
import { toastMessages } from '/src/lib/toast';
import { ReportingPeriodType, TResponseMetadata } from '/src/lib/types';
import { getPeriodFromDateString, pxToRem } from '/src/utils';

export type StatementListProps = {
  clientId: TClientId;
  client?: Client;
  title?: string;
  id?: string;
};

type CombinedStatementRow = {
  isCombined: true;
};

type ToggleRow = {
  isToggle: true;
  reportingPeriodId: number;
};

type StatementRow = IncentiveStatementListItem | CombinedStatementRow | ToggleRow;

export const StatementList: React.FC<StatementListProps> = (props) => {
  /** Props **/
  const { clientId, title, id } = props;

  /** Context **/
  const userStore = useUserStore();
  const globalStore = useGlobalStore();
  const { t } = useTranslation();

  /** State **/
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedPeriods, setExpandedPeriods] = useState<Set<number>>(new Set());

  const [reportingPeriods, setReportingPeriods] = useState<IncentiveStatementReportingPeriod[]>();
  const [metadata, setIncentiveStatementsMetadata] = useState<TResponseMetadata>();
  const [programs, setPrograms] = useState<Map<number, Program>>(new Map());
  const [request] = useState<TListIncentiveStatementReportingPeriodsRequest>({
    client_id: clientId,
    limit: 5,
  });

  /** Computed **/
  const hasReportingPeriods = !!reportingPeriods?.length;

  const togglePeriodExpansion = (periodId: number) => {
    setExpandedPeriods((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(periodId)) {
        newSet.delete(periodId);
      } else {
        newSet.add(periodId);
      }
      return newSet;
    });
  };

  /** Methods **/
  const fetchPrograms = async () => {
    try {
      const programsList = await ProgramService.list();
      const programsMap = new Map<number, Program>();
      programsList.forEach((program) => {
        programsMap.set(program.id, program);
      });
      setPrograms(programsMap);
    } catch (err) {
      console.error('Failed to fetch programs:', err);
    }
  };

  const fetchReportingPeriods = async (page?: number) => {
    if (!clientId || !userStore.user) return;

    try {
      const { meta, data } = await IncentiveStatementService.listIncentiveStatementReportingPeriods({
        ...request,
        page: page || request.page,
      });
      setReportingPeriods(data);
      setIncentiveStatementsMetadata(meta);
      setCurrentPage(meta.current_page);
    } catch (err) {
      globalStore.handleApiError(err as ApiError, toastMessages.listIncentiveStatements.error);
    } finally {
      setIsLoading(false);
    }
  };

  /** Effects **/
  useEffect(() => {
    fetchPrograms();
  }, []);

  useEffect(() => {
    if (!reportingPeriods) {
      fetchReportingPeriods(currentPage);
    }
  }, [reportingPeriods, currentPage]);

  useEffect(() => {
    if (!isLoading && metadata && currentPage !== metadata.current_page) {
      setIsLoading(true);
      fetchReportingPeriods(currentPage);
    }
  }, [isLoading, metadata, currentPage]);

  /** Render **/
  return (
    <Box id={id} direction="row" gap="medium" fill="horizontal">
      <Card>
        <CardHeader
          title={title || t('statements:statementList')}
          icon={<DocumentText size="24px" color="brand" />}
        ></CardHeader>
        <CardBody pad="none" gap="none">
          {isLoading && <LoadingSpinner />}
          {!isLoading && (
            <Box>
              {reportingPeriods?.map((reportingPeriod) => {
                const isExpanded = expandedPeriods.has(reportingPeriod.id);
                const hasSingleProgram = reportingPeriod.incentive_statements.length === 1;
                const tableData: StatementRow[] = [
                  {
                    isCombined: true,
                  },
                ];

                tableData.push({
                  isToggle: true,
                  reportingPeriodId: reportingPeriod.id,
                });

                if (isExpanded) {
                  tableData.push(...reportingPeriod.incentive_statements);
                }

                return (
                  <Box key={reportingPeriod.id}>
                    <Box pad={{ horizontal: 'medium', vertical: 'small' }} background="light-5">
                      <Text size="large">
                        {getPeriodFromDateString(
                          dayjs(reportingPeriod.end_reporting_quarter).format('YYYY-MM-DD'),
                          reportingPeriod.reporting_period_type_id === ReportingPeriodType.Yearly
                        )}
                      </Text>
                    </Box>
                    <DataTable
                      pad={{ horizontal: '1.5rem' }}
                      columns={[
                        {
                          property: 'program.name',
                          size: '30%',
                          header: <DataTableHeader title={t('common:program')} />,
                          render: (row: StatementRow) => {
                            if ('isCombined' in row) {
                              return (
                                <Box margin={{ vertical: pxToRem('14px') }}>
                                  <Text size="medium" fontFamily="Lato, sans-serif">
                                    {t('statements:allPrograms')}
                                  </Text>
                                </Box>
                              );
                            }
                            if ('isToggle' in row) {
                              return (
                                <Box
                                  direction="row"
                                  gap="xsmall"
                                  align="center"
                                  margin={{ vertical: pxToRem('14px') }}
                                  onClick={() => togglePeriodExpansion(reportingPeriod.id)}
                                  style={{ cursor: 'pointer' }}
                                  data-tour="view-individual-programs"
                                >
                                  {isExpanded ? <FormDown size="small" /> : <FormNext size="small" />}
                                  <Text size="small" style={{ textDecoration: 'none' }}>
                                    {isExpanded
                                      ? hasSingleProgram
                                        ? t('statements:hideIndividualProgram')
                                        : t('statements:hideIndividualPrograms')
                                      : hasSingleProgram
                                        ? t('statements:viewIndividualProgram')
                                        : t('statements:viewIndividualPrograms')}
                                  </Text>
                                </Box>
                              );
                            }
                            const program = programs.get(row.program_id);
                            return (
                              <Box margin={{ vertical: pxToRem('14px') }}>
                                <Text size="medium" fontFamily="Lato, sans-serif">
                                  {program?.name || row.program?.name || ''}
                                </Text>
                              </Box>
                            );
                          },
                        },
                        {
                          property: 'summary',
                          sortable: false,
                          size: '35%',
                          header: <DataTableHeader title={t('statements:individualStatement')} />,
                          render: (row: StatementRow) => {
                            if ('isToggle' in row) {
                              return null;
                            }
                            if ('isCombined' in row) {
                              return (
                                <ExternalLink
                                  to={`/clients/${clientId}/statements/reporting-period/${reportingPeriod.id}`}
                                  data-tour="individual-statement"
                                >
                                  <Box direction="row" gap="xsmall" align="center">
                                    <Print size="22px" />
                                    <Text fontFamily="Lato, sans-serif">{t('statements:summary')}</Text>
                                  </Box>
                                </ExternalLink>
                              );
                            }
                            return (
                              <ExternalLink
                                to={`/clients/${clientId}/statements/incentive/${row.id}`}
                                data-tour="individual-statement"
                              >
                                <Box direction="row" gap="xsmall" align="center">
                                  <Print size="22px" />
                                  <Text fontFamily="Lato, sans-serif">{t('statements:summary')}</Text>
                                </Box>
                              </ExternalLink>
                            );
                          },
                        },
                        {
                          property: 'details',
                          sortable: false,
                          size: '35%',
                          header: <DataTableHeader title={t('statements:detailedStatement')} />,
                          render: (row: StatementRow) => {
                            if ('isToggle' in row) {
                              return null;
                            }
                            if ('isCombined' in row) {
                              return (
                                <Box margin={{ vertical: pxToRem('14px') }}>
                                  <ExternalLink
                                    to={`/clients/${clientId}/statements/reporting-period/${reportingPeriod.id}/details`}
                                    data-tour="detailed-statement"
                                  >
                                    <Box direction="row" gap="xsmall" align="center">
                                      <Print size="22px" />
                                      <Text fontFamily="Lato, sans-serif">{t('statements:details')}</Text>
                                    </Box>
                                  </ExternalLink>
                                </Box>
                              );
                            }
                            return (
                              <Box margin={{ vertical: pxToRem('14px') }}>
                                <ExternalLink
                                  to={`/clients/${clientId}/statements/incentive/${row.id}/details`}
                                  data-tour="detailed-statement"
                                >
                                  <Box direction="row" gap="xsmall" align="center">
                                    <Print size="22px" />
                                    <Text fontFamily="Lato, sans-serif">{t('statements:details')}</Text>
                                  </Box>
                                </ExternalLink>
                              </Box>
                            );
                          },
                        },
                      ]}
                      data={tableData}
                      background={['white', 'light-6']}
                    />
                  </Box>
                );
              })}
            </Box>
          )}
          {!hasReportingPeriods && !isLoading && (
            <Box pad={{ horizontal: '1.5rem', vertical: '2rem' }} background="light-6" justify="center">
              <Text alignSelf="center" size="medium" fontFamily="Lato, sans-serif">
                {t('statements:noStatementsFound')}
              </Text>
            </Box>
          )}
          {!!metadata && !!currentPage && !!setCurrentPage && (
            <Box pad="1.5rem" flex="grow">
              <Pagination
                alignSelf="center"
                size="small"
                page={currentPage}
                step={metadata?.per_page}
                numberItems={metadata?.total}
                onChange={(e) => setCurrentPage(e.page)}
              />
            </Box>
          )}
        </CardBody>
      </Card>
    </Box>
  );
};
